import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLanguage } from "../../LanguageContext";

const WrioHeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const { t, language, setLanguage } = useLanguage();

  // Modal state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const modalRef = useRef(null);

  // Inline video player state
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const playerRef = useRef(null);
  const videoContainerRef = useRef(null);

  // Scroll reveal refs for below-the-fold sections
  const poweringRef = useRef(null);
  const [poweringVisible, setPoweringVisible] = useState(false);
  const videoRevealRef = useRef(null);
  const [videoRevealVisible, setVideoRevealVisible] = useState(false);

  // Load YouTube IFrame API once
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode.insertBefore(tag, firstScript);
    }
  }, []);

  // Initialize YouTube player
  const initPlayer = useCallback(() => {
    if (playerRef.current) return;
    const createPlayer = () => {
      playerRef.current = new window.YT.Player('wrio-inline-video', {
        videoId: 'ThZRtmd-DRU',
        playerVars: {
          controls: 0,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0,
          playsinline: 1,
          origin: window.location.origin,
        },
        events: {
          onStateChange: (event) => {
            setIsVideoPlaying(event.data === window.YT.PlayerState.PLAYING);
          },
        },
      });
    };
    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      window.onYouTubeIframeAPIReady = createPlayer;
    }
  }, []);

  // Init player when section scrolls into view
  useEffect(() => {
    const container = videoContainerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          initPlayer();
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [initPlayer]);

  const toggleVideo = () => {
    if (!playerRef.current) return;
    if (isVideoPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  };

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Powering section reveal
  useEffect(() => {
    const el = poweringRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setPoweringVisible(true); obs.unobserve(el); } },
      { threshold: 0.05, rootMargin: "0px 0px -20% 0px" }
    );
    obs.observe(el);
    return () => obs.unobserve(el);
  }, []);

  // Video label reveal
  useEffect(() => {
    const el = videoRevealRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVideoRevealVisible(true); obs.unobserve(el); } },
      { threshold: 0.05, rootMargin: "0px 0px -20% 0px" }
    );
    obs.observe(el);
    return () => obs.unobserve(el);
  }, []);

  // Close modal on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setShowVideoModal(false);
    };
    if (showVideoModal) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showVideoModal]);

  // Click-outside to close
  useEffect(() => {
    const onMouseDown = (e) => {
      if (!showVideoModal) return;
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setShowVideoModal(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showVideoModal]);

  const features = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
      ),
      titleKey: "features_quickSetup_title",
      descKey: "features_quickSetup_desc",
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>
      ),
      titleKey: "features_languages_title",
      descKey: "features_languages_desc",
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
      ),
      titleKey: "features_analytics_title",
      descKey: "features_analytics_desc",
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
      ),
      titleKey: "features_payments_title",
      descKey: "features_payments_desc",
    },
  ];

  const stats = [
    { value: "10K+", labelKey: "stat_happy_label" },
    { value: "2M+", labelKey: "stat_orders_label" },
    { value: "99.9%", labelKey: "stat_uptime_label" },
    { value: "4.9★", labelKey: "stat_rating_label" },
  ];

  const poweringItems = [
    { label: t("powering_items_restaurants"), icon: "🍕" },
    { label: t("powering_items_retail"), icon: "🛍️" },
    { label: t("powering_items_cafes"), icon: "☕" },
    { label: t("powering_items_bakeries"), icon: "🥐" },
    { label: t("powering_items_foodtrucks"), icon: "🚚" },
  ];

  return (
    <section className="relative overflow-hidden bg-white">
      {/* ── Geometric background ─────────────────────────────── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Faint grid */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(#0d9488 1px, transparent 1px), linear-gradient(90deg, #0d9488 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        {/* Soft orbs */}
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-teal-100 to-emerald-50 blur-3xl opacity-60" />
        <div className="absolute top-1/2 -left-48 w-[480px] h-[480px] rounded-full bg-gradient-to-tr from-cyan-50 to-teal-100 blur-3xl opacity-50" />
        <div className="absolute -bottom-24 right-1/3 w-96 h-96 rounded-full bg-gradient-to-t from-emerald-50 to-transparent blur-2xl opacity-70" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8 lg:pt-5 lg:pb-10">

        {/* ── Top pill badge ────────────────────────────────────── */}
        <div
          className={`flex justify-center mb-7 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
          }`}
        >
          <div className="inline-flex items-center gap-2.5 bg-gradient-to-r from-teal-600 to-emerald-500 text-white pl-2 pr-4 py-1.5 rounded-full text-sm font-semibold shadow-lg shadow-teal-200/60 tracking-tight">
            <span className="flex items-center justify-center w-5 h-5 bg-white/20 rounded-full text-xs">✨</span>
            {t("trustedBanner")}
          </div>
        </div>

        {/* ── Main two-column grid ─────────────────────────────── */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* LEFT ─ content */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"
            }`}
          >
            {/* Heading */}
            <h1
              className="text-[2.6rem] lg:text-5xl xl:text-[3.5rem] font-extrabold text-gray-950 mb-5 leading-[1.1] tracking-tight"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {t("heroHeadingLine1")}{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-teal-600 to-emerald-500 bg-clip-text text-transparent">
                  {t("heroHeadingHighlight")}
                </span>
                {/* Underline squiggle */}
                <svg
                  aria-hidden="true"
                  className="absolute -bottom-1.5 left-0 w-full"
                  viewBox="0 0 300 10"
                  fill="none"
                  preserveAspectRatio="none"
                >
                  <path d="M2 7C60 2 120 2 150 5C180 8 240 2 298 5" stroke="url(#uGrad)" strokeWidth="3" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="uGrad" x1="0" y1="0" x2="300" y2="0">
                      <stop stopColor="#0d9488" />
                      <stop offset="1" stopColor="#10b981" />
                    </linearGradient>
                  </defs>
                </svg>
              </span>{" "}
              {t("heroHeadingLine2")}
            </h1>

            {/* Sub */}
            <p className="text-lg text-gray-500 mb-8 leading-relaxed max-w-lg font-normal">
              {t("heroSub")}
            </p>

            {/* ── Feature cards ─────────────────────────── */}
            <div className="grid grid-cols-2 gap-2.5 mb-8">
              {features.map((f, i) => (
                <button
                  key={i}
                  onClick={() => setActiveFeature(i)}
                  className={`group flex items-start gap-3 p-3.5 rounded-xl border text-left reveal-scale reveal-delay-${i + 1} ${isVisible ? "revealed" : ""} transition-[opacity,transform,background,box-shadow,border-color] duration-[650ms,650ms,250ms,250ms,250ms] ${
                    activeFeature === i
                      ? "bg-gradient-to-br from-teal-600 to-emerald-500 border-transparent shadow-md shadow-teal-200/60 text-white"
                      : "bg-white border-gray-100 shadow-sm hover:border-teal-200 hover:shadow-md text-gray-700"
                  }`}
                  style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
                >
                  <span
                    className={`mt-0.5 flex-shrink-0 p-1.5 rounded-lg ${
                      activeFeature === i
                        ? "bg-white/20 text-white"
                        : "bg-teal-50 text-teal-600"
                    }`}
                  >
                    {f.icon}
                  </span>
                  <div>
                    <p className={`text-sm font-semibold leading-snug ${activeFeature === i ? "text-white" : "text-gray-800"}`}>
                      {t(f.titleKey)}
                    </p>
                    <p className={`text-xs mt-0.5 leading-snug ${activeFeature === i ? "text-teal-50" : "text-gray-400"}`}>
                      {t(f.descKey)}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {/* ── CTA Buttons ──────────────────────────── */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5 lg:mb-10">
              <a
                href="https://order.wrio.in/auth/login"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-gradient-to-r from-teal-600 to-emerald-500 text-white rounded-xl font-semibold text-base shadow-lg shadow-teal-200/60 hover:shadow-xl hover:shadow-teal-300/50 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden no-underline"
              >
                <span className="absolute inset-0 animate-shimmer opacity-0 group-hover:opacity-100" />
                {t("cta_create_store")}
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
              <button
                onClick={() => setShowVideoModal(true)}
                className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-gray-700 rounded-xl font-semibold text-base border border-gray-200 hover:border-teal-400 hover:text-teal-600 hover:-translate-y-0.5 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-teal-50 text-teal-600 group-hover:bg-teal-100 transition-colors">
                  <svg className="w-3.5 h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 5v10l8-5-8-5z" />
                  </svg>
                </span>
                {t("cta_watch_demo")}
              </button>
            </div>

            {/* ── Stats bar ──────────────────────────────
            <div className="grid grid-cols-4 gap-px bg-gray-100 rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
              {stats.map((s, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center justify-center py-4 bg-white hover:bg-teal-50/40 transition-colors duration-200"
                >
                  <p className="text-xl font-bold text-gray-900 tracking-tight">{s.value}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5 text-center leading-tight px-1">{t(s.labelKey)}</p>
                </div>
              ))}
            </div> */}
          </div>

          {/* RIGHT ─ device mockups */}
          <div
            className={`relative transition-all duration-1000 delay-300 hidden lg:block ${
              isVisible ? "translate-x-0 opacity-100" : "translate-x-10 opacity-0"
            }`}
          >
            <div className="relative flex items-center justify-center" style={{ minHeight: '520px' }}>
              {/* Soft glow blob behind mockups */}
              <div className="absolute inset-8 bg-gradient-to-br from-teal-200/30 to-emerald-200/20 rounded-full blur-3xl" />

              {/* Dashboard card */}
              <div className="absolute right-0 top-4 w-72 bg-white rounded-2xl shadow-2xl shadow-gray-200/80 border border-gray-100 overflow-hidden z-10 transform rotate-2 hover:rotate-0 transition-transform duration-500">
                {/* Window chrome */}
                <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-2 border-b border-gray-100">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 bg-white rounded-md text-[10px] px-2.5 py-1 text-gray-400 flex items-center gap-1.5 border border-gray-100">
                    <svg className="w-2.5 h-2.5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                    {t("mockup_dashboard_url")}
                  </div>
                </div>

                {/* Dashboard content */}
                <div className="p-4 bg-gray-50/50">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">{t("mockup_total_revenue")}</p>
                      <p className="text-2xl font-bold text-gray-900 leading-tight mt-0.5">₹24,580</p>
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-semibold mt-1 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
                        12.5%
                      </span>
                    </div>
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center text-white shadow-md shadow-teal-200/60">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                    </div>
                  </div>

                  {/* Mini chart */}
                  <div className="h-16 mb-3">
                    <svg viewBox="0 0 280 60" className="w-full h-full" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="cg" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#0d9488" stopOpacity="0.25" />
                          <stop offset="100%" stopColor="#0d9488" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path d="M0,52 C30,48 55,38 80,30 S130,18 160,15 S220,10 280,5" fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" />
                      <path d="M0,52 C30,48 55,38 80,30 S130,18 160,15 S220,10 280,5 L280,60 L0,60 Z" fill="url(#cg)" />
                      <circle cx="280" cy="5" r="4" fill="#0d9488" />
                      <circle cx="280" cy="5" r="7" fill="#0d9488" fillOpacity="0.2" />
                    </svg>
                  </div>

                  {/* Outlet rows */}
                  <div className="space-y-1.5">
                    {[
                      { name: t("mockup_outlet_alpha"), rev: "₹8,420", pct: 70 },
                      { name: t("mockup_outlet_beta"), rev: "₹6,340", pct: 52 },
                      { name: t("mockup_outlet_gamma"), rev: "₹4,820", pct: 40 },
                    ].map(({ name, rev, pct }, i) => (
                      <div key={i} className="flex items-center gap-2.5 bg-white p-2 rounded-lg border border-gray-50">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs flex-shrink-0 ${["bg-teal-500", "bg-emerald-500", "bg-cyan-500"][i]}`}>
                          {["🏪", "🛒", "🎯"][i]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-700 truncate">{name}</p>
                          <div className="h-1 bg-gray-100 rounded-full mt-1">
                            <div className={`h-full rounded-full ${["bg-teal-400", "bg-emerald-400", "bg-cyan-400"][i]}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <span className="text-xs font-bold text-gray-800 flex-shrink-0">{rev}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Phone mockup */}
              <div className="relative z-20 mr-20 transform -rotate-3 hover:rotate-0 transition-transform duration-500 animate-float">
                <div className="relative bg-gray-950 rounded-[2.5rem] p-1.5 shadow-2xl w-56 ring-1 ring-white/10">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-6 bg-gray-950 rounded-b-2xl z-10" />
                  <div className="bg-white rounded-[2.2rem] overflow-hidden">
                    {/* Status bar */}
                    <div className="bg-gradient-to-r from-teal-600 to-emerald-500 px-5 py-3 flex justify-between items-center">
                      <span className="text-white text-xs font-bold">9:41</span>
                      <div className="flex gap-1 text-white text-xs">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M1.5 8.5a13 13 0 0121 0M5 12a9 9 0 0114 0M8.5 15.5a5 5 0 017 0M12 19h.01" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><rect x="2" y="7" width="15" height="10" rx="2" /><path d="M22 11v2" strokeWidth="2" stroke="currentColor" strokeLinecap="round" /></svg>
                      </div>
                    </div>

                    {/* App content */}
                    <div className="bg-gradient-to-b from-teal-50/60 to-white p-4">
                      <div className="flex items-center justify-between mb-3.5">
                        <div>
                          <p className="text-[10px] text-gray-400">Good morning! 👋</p>
                          <p className="font-bold text-gray-900 text-sm leading-tight">Ice Cream Shop</p>
                        </div>
                        <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center text-white font-black text-sm shadow-md shadow-teal-200/60">W</div>
                      </div>

                      <div className="bg-white rounded-2xl shadow-md shadow-gray-100/80 border border-gray-50 p-3.5 mb-2.5">
                        <div className="flex justify-center mb-2.5">
                          <div className="w-20 h-24 bg-gradient-to-b from-amber-50 to-amber-100 rounded-xl flex items-center justify-center shadow-sm">
                            <span className="text-4xl">🍦</span>
                          </div>
                        </div>
                        <h3 className="text-sm font-bold text-gray-900 text-center">{t("vanilla_cone")}</h3>
                        <p className="text-[10px] text-gray-400 text-center">{t("premium_quality")}</p>
                        <div className="flex justify-center gap-0.5 my-1.5">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <span key={s} className="text-yellow-400 text-xs">★</span>
                          ))}
                        </div>
                        <div className="flex items-center justify-between mb-2.5">
                          <span className="text-xl font-black text-teal-600">₹149</span>
                          <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-full px-2.5 py-1">
                            <button className="text-teal-600 font-bold text-sm leading-none">−</button>
                            <span className="text-xs font-bold">1</span>
                            <button className="text-teal-600 font-bold text-sm leading-none">+</button>
                          </div>
                        </div>
                        <button className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-2.5 rounded-xl text-xs font-bold shadow-md shadow-teal-200/50 flex items-center justify-center gap-1.5">
                          {t("add_to_cart")} 🛒
                        </button>
                      </div>
                    </div>
                    <div className="bg-white py-2 flex justify-center border-t border-gray-50">
                      <div className="w-24 h-1 bg-gray-200 rounded-full" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating order notification */}
              <div className="absolute -left-2 top-[38%] bg-white rounded-2xl shadow-xl shadow-gray-200/80 border border-gray-100 p-3 z-30">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-800 leading-none">{t("new_order")}</p>
                    <p className="text-[11px] text-emerald-600 font-semibold mt-0.5">{t("new_order_amt")}</p>
                  </div>
                </div>
              </div>

              {/* Floating rating badge */}
              <div className="absolute -right-4 bottom-16 bg-white rounded-2xl shadow-xl shadow-gray-200/80 border border-gray-100 px-3 py-2 z-30 flex items-center gap-2">
                <div className="flex">
                  {[1,2,3,4,5].map(s => <span key={s} className="text-yellow-400 text-xs">★</span>)}
                </div>
                <span className="text-xs font-bold text-gray-800">4.9 Rating</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Video Showcase ──────────────────────────────────── */}
        <div ref={videoContainerRef} className="mt-12">
          {/* Section label — its own reveal */}
          <div
            ref={videoRevealRef}
            className={`text-center mb-8 reveal-up reveal-slow ${videoRevealVisible ? "revealed" : ""}`}
          >
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-100 px-4 py-2 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block animate-pulse" />
              See Wrio in Action
            </span>
          </div>

          <div className={`relative max-w-4xl mx-auto reveal-scale reveal-slow reveal-delay-1 ${videoRevealVisible ? "revealed" : ""}`}>
            <div
              className="relative rounded-2xl overflow-hidden shadow-2xl shadow-gray-300/40 ring-1 ring-black/5 bg-black group cursor-pointer"
              onClick={toggleVideo}
            >
              <div className="relative" style={{ paddingBottom: '56.25%' }}>
                <div
                  id="wrio-inline-video"
                  className="absolute inset-0 w-full h-full"
                  style={{ pointerEvents: 'none' }}
                />
              </div>
              {/* Play/Pause overlay */}
              <div
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                  isVideoPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
                }`}
                style={{ pointerEvents: 'none' }}
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-2xl ring-4 ring-white/30 transition-transform duration-300 group-hover:scale-110">
                  {isVideoPlaying ? (
                    <svg className="w-7 h-7 sm:w-8 sm:h-8 text-teal-700" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                  ) : (
                    <svg className="w-7 h-7 sm:w-8 sm:h-8 text-teal-700 ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
            {/* Glow */}
            <div className="absolute -inset-4 bg-gradient-to-r from-teal-400/10 to-emerald-400/10 rounded-3xl blur-2xl -z-10" />
          </div>
        </div>

        {/* ── Powered by industries ───────────────────────────── */}
        <div ref={poweringRef} className="mt-16 pb-6">
          <div className={`relative flex items-center gap-4 mb-7 reveal-fade ${poweringVisible ? "revealed" : ""}`}>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gray-200" />
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.15em] whitespace-nowrap">
              {t("powering_text")}
            </span>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gray-200" />
          </div>
          <div className="flex flex-wrap justify-center items-center gap-3">
            {poweringItems.map(({ label, icon }, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-100 shadow-sm hover:border-teal-300 hover:shadow-md hover:-translate-y-0.5 cursor-default reveal-up reveal-delay-${i + 1} ${poweringVisible ? "revealed" : ""}`}
              >
                <span className="text-base">{icon}</span>
                <span className="text-sm font-medium text-gray-600">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Video Modal ─────────────────────────────────────── */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] px-4">
          <div
            ref={modalRef}
            className="bg-white rounded-2xl overflow-hidden shadow-2xl max-w-3xl w-full relative animate-scaleIn"
          >
            <button
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-black rounded-full transition-colors z-20"
              onClick={() => setShowVideoModal(false)}
              aria-label="Close video"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="w-full h-64 sm:h-96">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/r9V188CXua8?autoplay=1&rel=0"
                title="Wrio Demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default WrioHeroSection;
